/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import { useCallback, useState } from 'react';

// Mantine
import {
	Image,
	Box,
	Drawer as NativeDrawer,
	Text,
	Avatar,
	Tooltip,
	Stack,
	Space,
	Group,
	Button,
	Spoiler,
} from '@mantine/core';

// Utils
import { truncateString } from '@utils/functions';

// Components
import { Onglet } from '@components/atoms/Onglet';

// Style
import classes from './DrawerAdditionalityProjectShowMore.module.css';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import PlaceholderLogo from '@assets/images/production-logo.svg';

// Icons
import { TAdditionalityProjectPopulated } from '../../types';
import isNil from 'lodash/isNil';
import {
	IconDownload,
	IconEdit,
	IconQuestionMark,
	IconTrash,
} from '@tabler/icons-react';
import { getCountryName } from '@utils/countries';
import { NOT_APPLICABLE } from '@config/constants/common';
import { VerifiedButton } from '@components/atoms';
import { TFileMetadata } from '@global-types/index';
import { DescriptionRenderer } from '@components/atoms/DescriptionRenderer';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type TDrawerAdditionalityProjectShowMore = {
	opened: boolean;
	position: 'right' | 'left';
	handleclose: () => void;
	handleactive: (status: boolean) => void;
	handledelete: () => void;
	handleupdate: () => void;
	data: Partial<TAdditionalityProjectPopulated>;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const DrawerAdditionalityProjectShowMore = (
	props: TDrawerAdditionalityProjectShowMore
) => {
	const {
		opened,
		handleclose,
		position,
		data,
		handleactive,
		handledelete,
		handleupdate,
	} = props;

	/* ******************************** STATES ******************************* */
	const [activeTab, setActiveTab] = useState(
		'generalInformations'
	);

	const {
		name,
		active,
		organization,
		entity,
		technology,
		location,
		cover,
		sites,
		social,
		publicSpecs,
		internalSpecs,
		description,
		financialNeeds,
		target_euro,
		pledged_euro,
		openingDate,
		dueDate,
	} = data;
	/* ******************************** HELPERS ******************************* */
	const handleCloseDrawerShowMore = useCallback(() => {
		setActiveTab('generalInformations');
		handleclose();
	}, []);

	const { t } = useTranslation();
	const { checkAcl } = useAuthorization();
	/* ******************************** CONSTANTS ******************************* */

	const ongletItems = [
		{
			label: t('global.generalInformations'),
			value: 'generalInformations',
			id: 'id-generalInformations',
			nbr: null,
		},
		{
			label: t('global.specifications'),
			value: 'specifications',
			id: '2',
			nbr:
				!isNil(sites) &&
				Array.isArray(sites) &&
				sites.length
					? sites.length
					: null,
		},
	];

	/* ****************************** RENDERING ****************************** */

	return (
		<NativeDrawer
			onClose={handleCloseDrawerShowMore}
			opened={opened}
			classNames={classes}
			overlayProps={{
				backgroundOpacity: 0.2,
				blur: 0.5,
			}}
			position={position}
		>
			<Box className={classes.wrapper}>
				{/* Header */}
				<Box className={classes.customHeader}>
					{isNil(cover) || cover == '' ? (
						<Box className={classes.containerLogo}>
							<Image
								className={classes.logo}
								src={PlaceholderLogo}
							/>
						</Box>
					) : (
						<Image
							className={classes.logoPicture}
							src={cover}
						/>
					)}
					<Box className={classes.containerTitle}>
						{!isNil(entity) && (
							<Text className={classes.customSubTitle}>
								{entity}
							</Text>
						)}

						{!isNil(name) && (
							<Text className={classes.customTitle}>
								{name}
							</Text>
						)}
					</Box>
				</Box>
				<Box className={classes.containerOnglet}>
					<Onglet
						items={ongletItems}
						onChange={(data: string | null) => {
							// eslint-disable-next-line @typescript-eslint/no-unused-expressions
							data !== null && setActiveTab(data);
						}}
					/>
				</Box>
				{/* Body */}
				<Box className={classes.customContent}>
					{activeTab == 'generalInformations' && (
						<Box className={classes.bodyDrawer}>
							{typeof active == 'boolean' && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.status')}
									</Text>
									<VerifiedButton
										onClick={() => {
											handleactive(!active);
										}}
										isverified={active}
									>
										{active
											? t('global.active')
											: t('global.inactive')}
									</VerifiedButton>
								</Box>
							)}
							{!isNil(location) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.location')}
									</Text>
									<Box
										className={
											classes.groupCountryInformations
										}
									>
										<Avatar
											className={classes.logoCountry}
											radius={'xl'}
											src={`https://flagcdn.com/w40/${location.countryCode.toLowerCase()}.png`}
											size={'xs'}
										>
											<IconQuestionMark />
										</Avatar>
										<Tooltip
											label={`${getCountryName(location.countryCode)} / ${location.place}`}
											disabled={
												location.countryCode &&
												location.place &&
												`${location.place}`.length > 10
													? false
													: true
											}
										>
											<Box
												className={
													classes.groupCountryTextInformations
												}
											>
												<Text
													className={
														classes.textCountryProject
													}
												>
													{getCountryName(
														location.countryCode
													)}
												</Text>

												<Text
													className={
														classes.textCountryPlaceProject
													}
												>
													{`/ ${location.place ? truncateString(location.place, 10) : NOT_APPLICABLE}`}
												</Text>
											</Box>
										</Tooltip>
									</Box>
								</Box>
							)}

							{!isNil(technology) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.projectType')}
									</Text>
									<Text className={classes.value}>
										{technology}
									</Text>
								</Box>
							)}

							{!isNil(financialNeeds) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.financialNeeds')}
									</Text>
									<Text className={classes.value}>
										{financialNeeds}
									</Text>
								</Box>
							)}

							{!isNil(target_euro) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.target_euro')}
									</Text>
									<Text className={classes.value}>
										{target_euro}
									</Text>
								</Box>
							)}

							{!isNil(pledged_euro) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.pledged_euro')}
									</Text>
									<Text className={classes.value}>
										{pledged_euro}
									</Text>
								</Box>
							)}

							{!isNil(openingDate) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.openingDate')}
									</Text>
									<Text className={classes.value}>
										{openingDate}
									</Text>
								</Box>
							)}

							{!isNil(dueDate) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.dueDate')}
									</Text>
									<Text className={classes.value}>
										{dueDate}
									</Text>
								</Box>
							)}

							{!isNil(organization) && (
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.organization')}
									</Text>
									<Text className={classes.value}>
										{organization}
									</Text>
								</Box>
							)}
						</Box>
					)}
					{activeTab == 'specifications' && (
						<Box className={classes.containerTabSecond}>
							<Space mt={'27px'} />
							<Box className={classes.containerSubHeader}>
								<Text className={classes.title}>
									{t('global.socialMedia')}
								</Text>
							</Box>
							<Space mt={'27px'} />

							<Stack gap={'16px'}>
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('Twitter')}
									</Text>
									{!isNil(social) &&
									!isNil(social.twitter) ? (
										<Text className={classes.value}>
											{social.twitter}
										</Text>
									) : (
										<Text className={classes.value}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>

								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('LinkedIn')}
									</Text>
									{!isNil(social) &&
									!isNil(social.linkedIn) ? (
										<Text className={classes.value}>
											{social.linkedIn}
										</Text>
									) : (
										<Text className={classes.value}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>

								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.website')}
									</Text>
									{!isNil(social) &&
									!isNil(social.website) ? (
										<Text className={classes.value}>
											{social.website}
										</Text>
									) : (
										<Text className={classes.value}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>
							</Stack>

							<Space mt={'27px'} />
							<Box className={classes.containerSubHeader}>
								<Text className={classes.title}>
									{t('global.titleTechnicalDocuments')}
								</Text>
							</Box>
							<Space mt={'27px'} />
							<Stack gap={'16px'}>
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t('global.technicalDocuments')}
									</Text>
									{!isNil(publicSpecs) &&
									publicSpecs &&
									Array.isArray(publicSpecs) &&
									publicSpecs.length > 0 ? (
										<Group justify="flex-end">
											{publicSpecs.map(
												(fileMetaData: TFileMetadata) => (
													<Tooltip
														disabled={
															!isNil(
																fileMetaData.filename
															) &&
															fileMetaData.filename
																.length <= 15
														}
														label={fileMetaData.filename}
													>
														<Button
															key={fileMetaData.id}
															onClick={() => {
																window.open(
																	fileMetaData.link,
																	'_blank'
																);
															}}
															variant="outline"
															leftSection={
																<IconDownload size={15} />
															}
															size={'xs'}
														>
															{truncateString(
																fileMetaData.filename,
																15
															)}
														</Button>
													</Tooltip>
												)
											)}
										</Group>
									) : (
										<Text className={classes.value}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>
								<Box className={classes.row}>
									<Text className={classes.label}>
										{t(
											'global.technicalDocumentsInternal'
										)}
									</Text>
									{!isNil(internalSpecs) &&
									internalSpecs &&
									Array.isArray(internalSpecs) &&
									internalSpecs.length > 0 ? (
										<Group justify="flex-end">
											{internalSpecs.map(
												(fileMetaData: TFileMetadata) => (
													<Tooltip
														disabled={
															!isNil(
																fileMetaData.filename
															) &&
															fileMetaData.filename
																.length <= 15
														}
														label={fileMetaData.filename}
													>
														<Button
															key={fileMetaData.id}
															onClick={() => {
																window.open(
																	fileMetaData.link,
																	'_blank'
																);
															}}
															variant="outline"
															leftSection={
																<IconDownload size={15} />
															}
															size={'xs'}
														>
															{truncateString(
																fileMetaData.filename,
																15
															)}
														</Button>
													</Tooltip>
												)
											)}
										</Group>
									) : (
										<Text className={classes.value}>
											{NOT_APPLICABLE}
										</Text>
									)}
								</Box>
							</Stack>

							<Space mt={'27px'} />
							<Box className={classes.containerSubHeader}>
								<Text className={classes.title}>
									{t('global.description')}
								</Text>
							</Box>
							<Space mt={'24px'} />
							<Spoiler
								maxHeight={120}
								showLabel={t('global.showMore')}
								hideLabel={t('global.hide')}
							>
								{description ? (
									<DescriptionRenderer
										description={description}
									/>
								) : (
									<Text className={classes.valueText}>
										{NOT_APPLICABLE}
									</Text>
								)}
							</Spoiler>
						</Box>
					)}
				</Box>
				<Stack gap={'10px'} pt={'xs'}>
					{checkAcl(ACLS.ADDITIONALITIES.UPDATE) && (
						<div>
							<Button
								onClick={handleupdate}
								leftSection={<IconEdit size={16} />}
								hiddenFrom="xxl"
								size="sm"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.updateProject')}
							</Button>
							<Button
								onClick={handleupdate}
								leftSection={<IconEdit size={20} />}
								size="md"
								visibleFrom="xxl"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.updateProject')}
							</Button>
						</div>
					)}
					{checkAcl(ACLS.ADDITIONALITIES.DELETE) && (
						<div>
							<Button
								disabled={
									sites &&
									Array.isArray(sites) &&
									sites?.length > 0
								}
								onClick={handledelete}
								leftSection={<IconTrash size={16} />}
								hiddenFrom="xxl"
								size="sm"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.deleteProject')}
							</Button>
							<Button
								disabled={
									sites &&
									Array.isArray(sites) &&
									sites?.length > 0
								}
								onClick={handledelete}
								leftSection={<IconTrash size={20} />}
								size="md"
								visibleFrom="xxl"
								fullWidth
								classNames={{
									root: classes.customDeleteButton,
								}}
							>
								{t('global.deleteProject')}
							</Button>
						</div>
					)}
				</Stack>
			</Box>
		</NativeDrawer>
	);
};
